/* The API client and the shapes it returns.
 *
 * One `request` helper so every call surfaces the server's own error text
 * rather than a bare "Failed to fetch" — the collectors fail in specific,
 * informative ways and that detail should reach the page. */

const BASE = '/api'

export class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
    this.name = 'ApiError'
  }
}

/** Called when any API call (other than the auth routes themselves) comes
 *  back 401 — the session has expired or was signed out elsewhere. The app
 *  registers a handler that drops it to the sign-in page, so a stale
 *  session does not show as an error box on every panel. */
let onUnauthorized: (() => void) | null = null
export function setUnauthorizedHandler(fn: (() => void) | null) { onUnauthorized = fn }

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response
  try {
    // The session is an HttpOnly cookie on the same origin, which fetch
    // sends by default; never set `credentials: 'omit'` here.
    res = await fetch(BASE + path, { credentials: 'same-origin', ...init })
  } catch {
    throw new ApiError(0, 'Could not reach the server — is it running on port 8721?')
  }
  if (!res.ok) {
    let detail = res.statusText
    try {
      const body = await res.json()
      detail = body.detail ?? body.error ?? detail
    } catch { /* non-JSON error body */ }
    // A wrong password on /auth/login and a missing session on /auth/me are
    // the auth flow's own answers, not an expired session.
    if (res.status === 401 && !path.startsWith('/auth/')) onUnauthorized?.()
    throw new ApiError(res.status, typeof detail === 'string' ? detail : JSON.stringify(detail))
  }
  return res.json() as Promise<T>
}

export const post = <T>(path: string, body?: unknown) =>
  request<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  })

// ------------------------------------------------------------- production
/** One unit instead of the plant: its own machines, totals and stoppages,
 *  with the plant-level meters, waste and overhead allocated to it by its
 *  share of each day's output. */
export type Unit = 1 | 2
export const UNIT_NAMES: Record<Unit, string> = { 1: 'UNIT - 1', 2: 'UNIT - 2' }
export const unitLabel = (u: Unit | null) => (u ? `Unit ${u}` : 'Both units')

export interface MonthSummary {
  empty: boolean
  month: string
  months: string[]
  benchmark: number
  /** 'UNIT - 1' / 'UNIT - 2' when one unit is in view, else null. */
  unit: string | null
  dates: string[]
  days: number
  series: {
    target: number[]; actual: number[]; waste: number[]
    /** Output against SITRA gps × spindles — the headline efficiency. */
    efficiency: number[]
    /** Output against the workbook's standard from the machine settings. */
    efficiency_settings: (number | null)[]
    /** Machine time run against installed, less stoppage loss. */
    utilization: number[]
    /** utilization x efficiency / 100 — the share of installed capacity earned. */
    util_x_eff: number[]
    machines_on: number[]
    u1: number[]; u2: number[]; eb: number[]; solar: number[]
    ukg: (number | null)[]; dow: string[]
  }
  /** `in_view` is the frames the page is reading — the plant's or one unit's. */
  installed: { total: number; 'UNIT - 1': number; 'UNIT - 2': number; in_view: number }
  totals: {
    target: number; actual: number; waste: number; shortfall: number
    efficiency: number; waste_pct: number; u1: number; u2: number
    target_settings: number; efficiency_settings: number | null
    /** Machine-shifts whose settings standard could not be believed. */
    unrated_settings_rows: number
    eb: number; solar: number; ukg_avg: number | null; avg_per_day: number
    /** Days with a meter reading, and the output on them — what power per
     *  kg is read over; a blank meter is not a day of zero draw. */
    metered_days: number; metered_kg: number
    /** The plant's output, and the unit's share of it (1 for the plant). */
    plant_actual: number; output_share: number
    utilization: number; util_x_eff: number
    /** Machine-shifts that ran on a count with no standard on file, of `rows`. */
    unrated_rows: number; rows: number
  }
  best_day: { date: string; actual: number; efficiency: number; util_x_eff: number } | null
  worst_day: { date: string; actual: number; efficiency: number; util_x_eff: number } | null
  pareto: { category: string; unit1: number; unit2: number; total: number; share: number }[]
  count_mix: { count: string; kg: number; share: number }[]
  stock: {
    as_of: string | null; on_hand: number; daily_totals: number[]; delta: number
    items: { item: string; qty: number; share: number }[]
  }
  table: {
    date: string; dow: string; target: number; actual: number; efficiency: number
    target_settings: number; efficiency_settings: number | null
    utilization: number; util_x_eff: number
    waste: number; waste_pct: number; ukg: number | null
    top_reason: string; top_reason_pct: number | null
  }[]
  /** What the summary sheet needs beyond the totals: production restated
   *  in 40s ((Ne / 40) ^ 1.5 per kilo), and the sales register's ex-mill
   *  ₹/kg — this month's and the trailing six months', plant-wide. */
  sheet: {
    conv_actual: number; conv_target: number
    conv_gps_actual: number | null; conv_gps_target: number | null
    avg_count: number | null
    /** 40s-converted output on the metered days. */
    conv_metered_kg: number
    sale: { price: number | null; invoices: number; price_6m: number | null; months_6m: string[] }
  }
}

export interface DayDetail {
  date: string
  dow: string
  unit: string | null
  /** The unit's share of the day's output (1 for the plant). */
  output_share: number
  totals: {
    target: number | null; actual: number | null; waste: number | null
    efficiency: number | null
    target_settings: number | null; efficiency_settings: number | null
  }
  items: {
    unit: string; sl_no: number; item: string; count: string; no_of_mcs: number
    target_prod: number; t_gps: number | null; actual_prod: number; a_gps: number | null
    efficiency: number | null
    target_settings: number; efficiency_settings: number | null
    /** The settings target in grams per spindle per shift, as `t_gps` is SITRA's. */
    t_gps_settings: number | null
    /** Machine-shifts on this line with no standard on file for the count. */
    unrated: number
  }[]
  stoppages: { category: string; unit1_pct: number | null; unit2_pct: number | null }[]
  yarn_stock: { item: string; qty: number }[]
  waste_stock: { category: string; produced: number; closing: number }[]
  unit_totals: {
    unit: string; total_mcs: number; target_prod: number; actual_prod: number
    target_settings: number; efficiency_settings: number | null
  }[]
  /** Meter readings keyed by label — EB, Solar, Total, UKG. */
  electricity: Record<string, number>
  utilization: {
    rows: {
      unit: string; machines_on: number; machines_installed: number
      utilization: number; efficiency: number | null; util_x_eff: number | null
      efficiency_settings: number | null
    }[]
    stop_loss: number
  }
}

export interface YearSummary {
  empty: boolean
  benchmark: number
  months: {
    month: string; days: number; target: number; actual: number; waste: number
    efficiency: number; utilization: number; util_x_eff: number
    target_settings: number; efficiency_settings: number | null
    waste_pct: number; avg_per_day: number
    ukg: number | null; top_reason: string
  }[]
  totals: {
    target: number; actual: number; waste: number; days: number
    shortfall: number; efficiency: number; waste_pct: number
    util_x_eff: number
    target_settings: number; efficiency_settings: number | null
  }
  best_month: { month: string; efficiency: number; actual: number } | null
}

export interface Inventory {
  days: { date: string; sheet: string; items: number; actual: number; stoppages: number; power: number }[]
  months: string[]
}

const qs = (params: Record<string, string | number | null | undefined>) => {
  const q = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => { if (v != null) q.set(k, String(v)) })
  const s = q.toString()
  return s ? `?${s}` : ''
}

export const production = {
  summary: (month?: string, unit?: Unit | null) =>
    request<MonthSummary>('/production/summary' + qs({ month, unit })),
  year: (unit?: Unit | null) => request<YearSummary>('/production/year' + qs({ unit })),
  day: (date: string, unit?: Unit | null) =>
    request<DayDetail>(`/production/day/${date}` + qs({ unit })),
  inventory: () => request<Inventory>('/production/inventory'),
  deleteDay: (date: string) =>
    request<{ ok: boolean; rows_removed: number }>(`/production/day/${date}`, { method: 'DELETE' }),
  upload: async (files: File[]) => {
    const fd = new FormData()
    files.forEach((f) => fd.append('files', f))
    return request<{
      ok: boolean
      loaded: {
        filename: string; days: number; rows_inserted: number; rows_replaced: number
        warnings: string[]
      }[]
      failed: { filename: string; error: string }[]
      months: string[]
    }>('/production/upload', { method: 'POST', body: fd })
  },
  exportUrl: () => BASE + '/production/export',
}

// ----------------------------------------------------------------- cotton
export interface CottonSeries {
  i: number; key: string; src: string; basis: string; kind: string
  region: string | null; market: string | null; variety: string | null
  std: string | null; grade: string | null; staple: string | null
  mm: number | null; unit: string | null; label: string; short: string
}

/** [seriesIndex, dateIndex, inrPerCandy, nativePrice, lintEquivalent] */
export type CottonPoint = [number, number, number, number | null, number | null]

export interface CottonData {
  generated: string
  subtitle: string
  candyKg: number
  got: number
  rowCount: number
  sources: string[]
  sourceLabels: Record<string, string>
  series: CottonSeries[]
  dates: string[]
  points: CottonPoint[]
  notes: [string, string][]
}

export interface FetchResult {
  ok: boolean
  days: number
  rows: number
  sources: { source: string; ok: boolean; rows: number; skipped: boolean; error?: string }[]
  data: CottonData
}

export const cotton = {
  data: () => request<CottonData>('/cotton/data'),
  coverage: () =>
    request<{
      sources: { source: string; n: number; days: number; first_date: string; last_date: string; series: number }[]
      recent: { source: string; ran_at: string; ok: boolean; rows: number; detail: string | null }[]
    }>('/cotton/coverage'),
  fetch: (force = false) => post<FetchResult>(`/cotton/fetch?force=${force}`),
  importCsv: (body: {
    csv: string; series: string; label?: string; note?: string
    period: 'day' | 'week' | 'month'; replace?: string
  }) => post<{ ok: boolean; rows: number; removed: number; first: string; last: string; data: CottonData }>(
    '/cotton/import', body),
  dropSeries: (key: string) =>
    request<{ ok: boolean; removed: number; data: CottonData }>(
      `/cotton/series/${encodeURIComponent(key)}`, { method: 'DELETE' }),
}

// ---------------------------------------------------------- profitability
export interface ProfitInputs {
  u1: number; u2: number; oh: number; candy: number
  yarn: number; real: number; gps: number
  /** Share of installed machine hours actually run. */
  util: number
  /** Output against standard while running. */
  eff: number
}

export interface Product {
  id: string; name: string; unit: number; mcs: number
  yarn: number; candy: number; real: number; gps: number
  /** The count the row spins, as a key into the market's counts. Absent on
   *  a free-text row, which then has no market price to follow. */
  count?: number | null
  quality?: string | null
  /** False once someone types a yarn price over the market's. */
  yarnAuto?: boolean
  /** Which cotton (by id) the row is spun from. */
  cotton?: string
}

/** One cotton the mix is spun from — the only place its price is entered. */
export interface CottonLot {
  id: string
  /** CAI grade the price follows; null on a cotton priced by hand only. */
  grade: string | null
  /** True while the price follows the grade's latest CAI rate. */
  auto: boolean
  /** The typed price, ₹ per candy — what `auto: false` uses. */
  candy: number
}

/** The single-product case's count and where its two prices come from. */
export interface SinglePricing {
  count: number | null; quality: string | null
  grade: string | null
  yarnAuto: boolean; cottonAuto: boolean
}

export interface MarketYarn { price: number; basis: 'published' | 'estimated'; note: string }
export interface MarketGrade {
  key: string; label: string; market: string; std: string
  mm: number | null; date: string; candy: number
}
export interface MarketCount {
  count: number; quality: string; label: string; name: string
  yarn: MarketYarn | null
  /** Default cotton grade for the count, by staple. */
  grade: string | null
  /** SITRA grams per spindle per 8-hour shift for the count, if on file. */
  gps: number | null
}
export interface Market {
  cotton: { as_of: string | null; grades: MarketGrade[] }
  yarn: {
    as_of: string | null; source: string | null; loaded_at: string | null
    quotes: { count: number; type: string; price: number; date: string; fresh: boolean }[]
  }
  counts: MarketCount[]
  fresh_months: number
}

export interface VarMeta {
  id: keyof ProfitInputs; label: string; min: number; max: number
  step: number; fmt: string; note: string
}

export interface ProfitLine {
  id: string; name: string; unit: number; mcs: number; spindles: number
  prod: number; cotton_per_kg: number; overhead: number; revenue: number
  cotton: number; cost: number; profit: number; margin: number
  contribution: number; share: number
}

export interface ProfitResult {
  machines: number; spindles: number; prod: number
  cotton_per_kg: number; overhead_day: number
  revenue: number; cotton: number; cost: number
  profit: number; margin: number; contribution: number
  breakeven: number | null
  max_prod?: number
  lines?: ProfitLine[]
  over_allocated?: boolean
}

export interface Scenario {
  id: number
  name: string
  mode: string
  payload: {
    inputs?: ProfitInputs
    products?: Product[]
    util?: number
    eff?: number
    ohParts?: Record<string, number>
    /** Absent on a case saved before cotton was priced in one place. */
    lots?: CottonLot[]
    single?: SinglePricing
  }
  updated_at: string | null
}

export interface OverheadHead {
  id: string; label: string; note: string; max: number
  monthly: number; allocated_day: number
  /** Per kilo of yarn produced. */
  per_kg: number | null
  /** Per kilo of raw cotton fed in — what the per-cotton table breaks down. */
  per_kg_cotton: number | null
}

export interface PerCottonRow {
  label: string; cotton: number | null; yarn: number | null
  unit: 'kg' | 'inr'; note: string
}

export interface ComputeResponse {
  overhead: {
    rows: OverheadHead[]
    total_monthly: number; allocated_day: number
    per_kg: number | null; per_kg_cotton: number | null; share: number
  }
  per_cotton: {
    cotton_kg: number
    yarn_kg: number
    yarn_per_cotton_kg: number | null
    /** Kilos of cotton behind one kilo of yarn — the inverse of realization. */
    cotton_per_yarn_kg: number | null
    realization: number | null
    rows: PerCottonRow[]
    /** Per kilo of yarn, one row per count. */
    lines: {
      id: string; name: string; meta: string
      yarn_kg: number; cotton_kg: number; cotton_per_yarn_kg: number | null
      cotton: number | null; revenue: number | null; contribution: number | null
      overhead: number | null; profit: number | null
    }[]
  }
  mode: 'single' | 'mix'
  period: 'day' | 'month'
  scale: number
  result: ProfitResult
  tornado: { key: string; label: string; low: number; high: number; span: number; rising: boolean; fmt: string }[]
  sweep: {
    key: string; label: string; fmt: string; min: number; max: number; current: number
    points: { x: number; revenue: number; cost: number; profit: number }[]
    zero_crossing: number | null
  }
  axes: { key: string; label: string }[]
  ledger: { label: string; day: number | null; month: number | null; kind: string; basis: string }[] | null
  candy_kg: number
}

export const profitability = {
  defaults: () =>
    request<{
      inputs: ProfitInputs
      single: { count: number; quality: string }
      mix: Product[]
      vars: VarMeta[]
      overhead_heads: { id: string; label: string; value: number; max: number; note: string }[]
      constants: Record<string, number>
    }>('/profitability/defaults'),
  compute: (body: {
    mode: 'single' | 'mix'; period: 'day' | 'month'
    inputs: ProfitInputs; products: Product[]
    oh: number; util: number; eff: number
    /** Overhead by head; omit to use the single `oh` total. */
    overhead?: Record<string, number>
    axis?: string | null
  }) => post<ComputeResponse>('/profitability/compute', body),
  /** Latest cotton rate per grade and yarn price per count — the autofill. */
  market: () => request<Market>('/profitability/market'),
  /** Replaces the count-wise yarn prices with the sheet in `file` (xlsx). */
  uploadYarnPrices: async (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return request<{
      ok: boolean; source: string; rows: number; rows_replaced: number
      series: number; last_month: string; warnings: string[]
    }>('/profitability/yarn-prices', { method: 'POST', body: fd })
  },
  scenarios: () => request<Scenario[]>('/profitability/scenarios'),
  save: (body: { name: string; mode: string; payload: any }) =>
    post<{ id: number; name: string }>('/profitability/scenarios', body),
  remove: (id: number) =>
    request<{ ok: boolean }>(`/profitability/scenarios/${id}`, { method: 'DELETE' }),
}

export const health = () =>
  request<{ ok: boolean; db: string; spa: boolean }>('/health')
